import type { MediaType } from '@/cmsBackend/transformers/types/featureAnnouncement';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './MediaCarousel.module.scss';

export interface MediaItem {
  type: MediaType;
  src: string;
  alt: string;
}

export interface MediaCarouselProps {
  items: MediaItem[];
  autoAdvanceMs?: number;
  className?: string;
}

export const MediaCarousel: React.FC<MediaCarouselProps> = ({
  items,
  autoAdvanceMs = 5000,
  className,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const hasMultiple = items.length > 1;
  const activeItem = items[activeIndex];

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    if (hasMultiple) {
      timerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % items.length);
      }, autoAdvanceMs);
    }
  }, [hasMultiple, items.length, autoAdvanceMs, clearTimer]);

  // Manage auto-advance based on active item type
  useEffect(() => {
    if (!hasMultiple) return;

    if (activeItem?.type === 'video') {
      // For videos, don't use timer — advance happens via onEnded
      clearTimer();
    } else {
      startTimer();
    }

    return clearTimer;
  }, [activeIndex, activeItem?.type, hasMultiple, clearTimer, startTimer]);

  // Imperatively play/pause videos when activeIndex changes
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (index === activeIndex) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [activeIndex]);

  const setVideoRef = useCallback((index: number, el: HTMLVideoElement | null) => {
    if (el) {
      videoRefs.current.set(index, el);
    } else {
      videoRefs.current.delete(index);
    }
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const goPrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  if (!items.length) return null;

  return (
    <div className={classNames(styles.carousel, className)}>
      {items.map((item, index) => (
        <div
          key={item.src}
          className={classNames(
            styles.carousel__slide,
            index === activeIndex && styles['carousel__slide--active'],
          )}
        >
          {item.type === 'video' ? (
            <video
              ref={(el) => setVideoRef(index, el)}
              src={item.src}
              muted
              playsInline
              onEnded={hasMultiple ? goNext : undefined}
              aria-label={item.alt}
            />
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={item.src} alt={item.alt} loading={index === 0 ? 'eager' : 'lazy'} />
          )}
        </div>
      ))}

      {hasMultiple && (
        <>
          <button
            className={classNames(styles.carousel__arrow, styles['carousel__arrow--left'])}
            onClick={goPrev}
            aria-label="Previous slide"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M10 12L6 8L10 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className={classNames(styles.carousel__arrow, styles['carousel__arrow--right'])}
            onClick={goNext}
            aria-label="Next slide"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M6 4L10 8L6 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className={styles.carousel__indicators}>
            {items.map((_, index) => (
              <button
                key={index}
                className={classNames(
                  styles.carousel__dot,
                  index === activeIndex && styles['carousel__dot--active'],
                )}
                onClick={() => {
                  setActiveIndex(index);
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
